import {
  Component,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { DigitsOnlyDirective } from '../../core/directives';
import { StockSnapshot } from '../../core/models/inventory.models';
import { ExportService } from '../../core/services/export.service';
import { InventoryService } from '../../core/services/inventory.service';
import { SettingsService } from '../../core/services/settings.service';
import { confirmAction } from '../../core/utils/confirm.util';
import {
  defaultBranchFilter,
  isBranchFilterLocked,
  matchesBranchFilter,
  resetBranchFilter,
} from '../../core/utils/branch-scope.util';
import { AuthService } from '../auth/auth.service';
import { currentTimestamp } from '../../core/utils/format.util';
import { environment } from '../../environments/environment';
import {
  assertCanTake,
  parseNonNegativeNumber,
  parsePositiveInt,
  remainingAvailable,
} from '../../core/utils/stock-qty.util';

export interface StockSnapshotView extends StockSnapshot {
  itemId: string;
  inventoryItemId?: string;
  branch: string;
  branchId?: string;
  reservedQuantity: number;
}

@Component({
  selector: 'app-current-stock',
  standalone: true,
  imports: [CommonModule, FormsModule, DigitsOnlyDirective],
  templateUrl: './current-stock.component.html',
  styleUrl: './current-stock.component.css',
})
export class CurrentStockComponent implements OnInit {
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);
  private readonly exporter = inject(ExportService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly timestamp = currentTimestamp;
  readonly branches = this.settings.scopedBranches;
  readonly locations = this.settings.scopedLocations;
  readonly branchFilterLocked = computed(() => isBranchFilterLocked(this.auth));
  readonly categories = this.settings.allCategories;

  // ============================================
  // STATE
  // ============================================
  readonly search = signal('');
  readonly branchFilter = signal('all');
  readonly locationFilter = signal('all');
  readonly categoryFilter = signal('all');
  readonly page = signal(1);
  readonly pageSize = 12;

  // Modal signals
  readonly showStockModal = signal(false);
  readonly selectedStock = signal<StockSnapshotView | null>(null);
  readonly adjustmentType = signal<'add' | 'remove' | 'set'>('add');
  readonly adjustmentQuantity = signal<number>(1);
  readonly adjustmentReason = signal<string>('');
  readonly adjustmentNotes = signal<string>('');
  readonly showToast = signal(false);
  readonly toastMessage = signal('');
  readonly toastType = signal<'success' | 'error' | 'info'>('success');

  readonly allStockSnapshots = computed<StockSnapshotView[]>(() => {
    const fromApi: StockSnapshotView[] = this.inventory
      .allStockSnapshots()
      .filter(
        (snapshot) =>
          !!(
            snapshot.itemName ||
            snapshot.itemCode ||
            snapshot.inventoryItemId
          ),
      )
      .map((snapshot) => ({
        ...snapshot,
        itemId: snapshot.id,
        inventoryItemId: (
          snapshot as StockSnapshot & { inventoryItemId?: string }
        ).inventoryItemId,
        branch: snapshot.branch ?? '',
        branchId: (snapshot as StockSnapshot & { branchId?: string }).branchId,
        reservedQuantity: snapshot.reservedQuantity ?? 0,
      }));

    // When Current Stock API returned real balances, never invent catalog placeholders —
    // those use Register Items branch totals and hide per-location transfer changes.
    if (fromApi.length > 0) {
      return fromApi;
    }

    const placeholders: StockSnapshotView[] = this.inventory
      .allItems()
      .map((item) => ({
        id: `catalog-${item.id}`,
        itemId: `catalog-${item.id}`,
        inventoryItemId: item.id,
        itemCode: item.code,
        itemName: item.name,
        category: item.category,
        branch: item.branch ?? '',
        branchId: item.branchId,
        location: item.location ?? '',
        locationId: item.locationId,
        quantity: Math.max(0, item.stockLevel ?? 0),
        reservedQuantity: 0,
        unit: item.unit,
        unitValue: item.price,
        stockStatus:
          (item.stockLevel ?? 0) > 0 ? ('ok' as const) : ('critical' as const),
        lastUpdated: item.lastUpdated,
      }));

    return placeholders;
  });

  constructor() {
    effect(() => {
      const branches = this.branches();
      if (!branches.length) {
        return;
      }
      this.branchFilter.set(defaultBranchFilter(this.auth, branches));
    });
  }

  ngOnInit(): void {
    if (!environment.useMockData) {
      this.settings.refreshFromApi();
      this.inventory.loadItemMasterAndStock().subscribe();
    }
  }

  readonly filteredSnapshots = computed(() => {
    const search = this.search().trim().toLowerCase();
    const category = this.categoryFilter();
    const location = this.locationFilter();
    const branch = this.branchFilter();

    return this.allStockSnapshots().filter((row) => {
      const matchesSearch =
        !search ||
        row.itemCode.toLowerCase().includes(search) ||
        row.itemName.toLowerCase().includes(search) ||
        row.category.toLowerCase().includes(search) ||
        row.branch.toLowerCase().includes(search) ||
        row.location.toLowerCase().includes(search);
      const matchesCategory = category === 'all' || row.category === category;
      const matchesLocation =
        location === 'all' ||
        row.location.trim().toLowerCase() === location.trim().toLowerCase() ||
        (!!row.locationId &&
          this.locations().some(
            (loc) =>
              loc.id === row.locationId &&
              loc.name.trim().toLowerCase() === location.trim().toLowerCase(),
          ));
      const matchesBranch =
        branch === 'all' ||
        matchesBranchFilter(
          branch,
          row.branch,
          row.branchId,
          this.branches(),
        ) ||
        (!row.branchId?.trim() && !row.branch?.trim());
      return (
        matchesSearch && matchesCategory && matchesLocation && matchesBranch
      );
    });
  });

  readonly stats = computed(() => {
    const rows = this.filteredSnapshots();
    const lowOrOut = rows.filter(
      (row) =>
        row.stockStatus === 'low' ||
        row.stockStatus === 'critical' ||
        row.quantity === 0,
    ).length;
    return {
      totalItems: rows.length,
      available: rows.filter((row) => row.quantity > 0).length,
      reserved: rows.filter((row) => (row.reservedQuantity || 0) > 0).length,
      lowOrOut,
    };
  });

  readonly paginatedSnapshots = computed(() => {
    const start = (this.page() - 1) * this.pageSize;
    return this.filteredSnapshots().slice(start, start + this.pageSize);
  });

  readonly totalPages = computed(() =>
    Math.max(1, Math.ceil(this.filteredSnapshots().length / this.pageSize)),
  );

  readonly paginationStart = computed(() =>
    this.filteredSnapshots().length === 0
      ? 0
      : (this.page() - 1) * this.pageSize + 1,
  );

  readonly paginationEnd = computed(() =>
    Math.min(this.page() * this.pageSize, this.filteredSnapshots().length),
  );

  readonly pageNumbers = computed(() =>
    Array.from({ length: this.totalPages() }, (_, i) => i + 1),
  );

  // ============================================
  // HELPER METHODS
  // ============================================
  getReservedQuantity(snapshot: StockSnapshotView): number {
    return snapshot.reservedQuantity || 0;
  }

  getAvailableQuantity(snapshot: StockSnapshotView): number {
    return remainingAvailable(
      snapshot.quantity,
      snapshot.reservedQuantity || 0,
    );
  }

  calculateNewStock(): number {
    const current = this.selectedStock()?.quantity || 0;
    const quantity = this.adjustmentQuantity();
    const type = this.adjustmentType();

    if (type === 'add') return current + quantity;
    if (type === 'remove') return current - quantity;
    return quantity;
  }

  onAdjustmentQuantityChange(raw: unknown): void {
    if (this.adjustmentType() === 'set') {
      const parsed = parseNonNegativeNumber(raw, 'Quantity');
      if (!parsed.ok) {
        this.showToastMessage(
          parsed.message ?? 'Enter a valid quantity',
          'error',
        );
        return;
      }
      this.adjustmentQuantity.set(parsed.value);
      return;
    }
    const parsed = parsePositiveInt(raw, 'Quantity');
    if (!parsed.ok) {
      this.showToastMessage(
        parsed.message ?? 'Enter a valid quantity',
        'error',
      );
      return;
    }
    this.adjustmentQuantity.set(parsed.value);
  }

  onSearchChange(): void {
    this.page.set(1);
  }

  onFilterChange(): void {
    this.page.set(1);
  }

  clearFilters(): void {
    this.search.set('');
    this.branchFilter.set(resetBranchFilter(this.auth, this.branches()));
    this.locationFilter.set('all');
    this.categoryFilter.set('all');
    this.page.set(1);
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.page.set(page);
    }
  }

  stockCardClass(snapshot: StockSnapshotView): string {
    if (snapshot.quantity === 0) {
      return 'out';
    }
    if (snapshot.stockStatus === 'critical') {
      return 'reorder';
    }
    if (snapshot.stockStatus === 'low') {
      return 'low';
    }
    return 'ok';
  }

  stockStatusLabel(snapshot: StockSnapshotView): string {
    if (String(snapshot.id).startsWith('catalog-') && snapshot.quantity === 0) {
      return 'Not received';
    }
    if (snapshot.quantity === 0) {
      return 'Out of Stock';
    }
    if (snapshot.stockStatus === 'critical') {
      return 'Reorder';
    }
    if (snapshot.stockStatus === 'low') {
      return 'Low Stock';
    }
    return 'OK';
  }

  canTrack(snapshot: StockSnapshotView): boolean {
    const id = snapshot.inventoryItemId || snapshot.itemId;
    return !!id && !String(id).startsWith('catalog-') && Number(id) > 0;
  }

  openItemHistory(snapshot: StockSnapshotView, event: Event): void {
    event.stopPropagation();
    const id = snapshot.inventoryItemId || snapshot.itemId;
    if (!this.canTrack(snapshot)) {
      this.showToastMessage(
        'Receive this item first, then you can track movements.',
        'info',
      );
      return;
    }
    void this.router.navigate(['/item-history', id]);
  }

  // ============================================
  // MODAL METHODS
  // ============================================
  openStockModal(snapshot: StockSnapshotView): void {
    if (String(snapshot.id).startsWith('catalog-')) {
      this.showToastMessage(
        'This item has no stock balance yet. Receive stock via Goods Received first.',
        'info',
      );
      return;
    }
    this.selectedStock.set(snapshot);
    this.adjustmentType.set('add');
    this.adjustmentQuantity.set(1);
    this.adjustmentReason.set('');
    this.adjustmentNotes.set('');
    this.showStockModal.set(true);
    document.body.style.overflow = 'hidden';
  }

  closeStockModal(): void {
    this.showStockModal.set(false);
    this.selectedStock.set(null);
    document.body.style.overflow = 'auto';
  }

  closeModalOnBackdrop(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('modal-overlay')) {
      this.closeStockModal();
    }
  }

  submitStockAdjustment(): void {
    const stock = this.selectedStock();
    if (!stock) {
      this.showToastMessage('No stock item selected', 'error');
      return;
    }

    const type = this.adjustmentType();
    let quantity: number;
    if (type === 'set') {
      const parsed = parseNonNegativeNumber(
        this.adjustmentQuantity(),
        'Quantity',
      );
      if (!parsed.ok) {
        this.showToastMessage(
          parsed.message ?? 'Please enter a valid quantity',
          'error',
        );
        return;
      }
      quantity = parsed.value;
    } else {
      const parsed = parsePositiveInt(this.adjustmentQuantity(), 'Quantity');
      if (!parsed.ok) {
        this.showToastMessage(
          parsed.message ?? 'Please enter a valid quantity',
          'error',
        );
        return;
      }
      quantity = parsed.value;
    }

    const reason = this.adjustmentReason();
    if (!reason) {
      this.showToastMessage(
        'Please select a reason for the adjustment',
        'error',
      );
      return;
    }

    const available = remainingAvailable(
      stock.quantity,
      stock.reservedQuantity || 0,
    );
    let newQuantity = stock.quantity;

    if (type === 'add') {
      newQuantity = stock.quantity + quantity;
    } else if (type === 'remove') {
      const check = assertCanTake(available, quantity, stock.itemName);
      if (!check.ok) {
        this.showToastMessage(
          check.message ?? 'Cannot remove more than remaining available',
          'error',
        );
        return;
      }
      newQuantity = stock.quantity - quantity;
    } else if (type === 'set') {
      if (quantity < (stock.reservedQuantity || 0)) {
        this.showToastMessage(
          `Cannot set on-hand below ${stock.reservedQuantity || 0} reserved for ${stock.itemName}.`,
          'error',
        );
        return;
      }
      newQuantity = quantity;
    }

    const confirmed = confirmAction(
      `Confirm ${type} adjustment for "${stock.itemName}"?\n\n` +
        `Current: ${stock.quantity}\n` +
        `Available: ${available}\n` +
        `New: ${newQuantity}\n` +
        `Reason: ${reason}\n` +
        `${this.adjustmentNotes() ? `Notes: ${this.adjustmentNotes()}` : ''}`,
    );

    if (!confirmed) return;

    const stockBalanceId = Number(stock.id || stock.itemId);
    if (!Number.isFinite(stockBalanceId) || stockBalanceId <= 0) {
      this.showToastMessage(
        'Cannot adjust this row: missing StockBalanceID. Refresh Current Stock and try again.',
        'error',
      );
      return;
    }

    const stockAny = stock as StockSnapshotView & {
      inventoryItemId?: string;
      branchId?: string;
    };
    const branchId =
      stockAny.branchId ||
      this.branches().find((row) => row.name === stock.branch)?.id ||
      String(this.auth.currentBranchId() || '');
    const locationId = this.locations().find(
      (row) => row.name === stock.location,
    )?.id;

    this.inventory
      .adjustStockViaApi({
        stockBalanceId: String(stockBalanceId),
        inventoryItemId: stockAny.inventoryItemId,
        branchId,
        locationId,
        newQuantity,
        reason,
        notes: this.adjustmentNotes(),
      })
      .subscribe((result) => {
        if (result.success) {
          this.showToastMessage(
            `Stock adjusted successfully! ${stock.itemName} updated from ${stock.quantity} to ${newQuantity}`,
            'success',
          );
          this.closeStockModal();
        } else {
          this.showToastMessage(
            result.message ?? 'Failed to adjust stock',
            'error',
          );
        }
      });
  }

  showToastMessage(
    message: string,
    type: 'success' | 'error' | 'info' = 'success',
  ): void {
    this.toastMessage.set(message);
    this.toastType.set(type);
    this.showToast.set(true);
    setTimeout(() => this.showToast.set(false), 4000);
  }

  exportData(): void {
    const rows = this.filteredSnapshots().map((row) => ({
      'Item Code': row.itemCode,
      'Item Name': row.itemName,
      Category: row.category,
      Branch: row.branch,
      Location: row.location,
      'On Hand': row.quantity,
      Reserved: row.reservedQuantity || 0,
      Available: remainingAvailable(row.quantity, row.reservedQuantity || 0),
      Status: this.stockStatusLabel(row),
    }));

    if (rows.length === 0) {
      this.showToastMessage(
        'No stock items match the current filters.',
        'info',
      );
      return;
    }

    this.exporter.downloadCsv(
      `current-stock-${new Date().toISOString().slice(0, 10)}`,
      rows,
    );
  }

  printPage(): void {
    this.exporter.printPage();
  }
}
